import type { ICancellationPolicyOption, ILandOwner, ILandType, IListing, ISite, IUser } from '@curbnturf/objects';

export namespace listingComplete {
  const missingAddress = (contact: IUser | ILandOwner): boolean => {
    return (
      !contact ||
      !contact.firstName ||
      !contact.lastName ||
      !contact.address ||
      !contact.address.address ||
      !contact.address.city ||
      !contact.address.state ||
      !contact.phone
    );
  };

  const testBookingProcess = (listing: IListing): { messages: string[]; completeness: number } => {
    let completeness = 0;
    const messages: string[] = [];

    if (!listing) {
      return { messages, completeness };
    }

    if (listing.reservationProcess) {
      completeness += 3;
    }

    if (listing.checkInProcess) {
      completeness += 3;
    }

    return { messages, completeness };
  };

  const testBookingRange = (listing: IListing): { messages: string[]; completeness: number } => {
    let completeness = 0;
    const messages: string[] = [];

    if (!listing) {
      return { messages, completeness };
    }

    if (listing.bookingWindow) {
      completeness += 3;
    } else {
      messages.push('Missing Booking Window');
    }

    if (listing.reserveDaysInAdvance || listing.reserveDaysInAdvance === 0) {
      completeness += 3;
    } else {
      messages.push('Missing Reservation Advance Notice');
    }

    return { messages, completeness };
  };

  const testCheckInOut = (listing: IListing): { messages: string[]; completeness: number } => {
    let completeness = 0;
    const messages: string[] = [];

    if (!listing) {
      return { messages: ['Missing Check In Time', 'Missing Check Out Time'], completeness };
    }

    if (listing.checkIn) {
      completeness += 3;
    } else {
      messages.push('Missing Check In Time');
    }

    if (listing.checkOut) {
      completeness += 3;
    } else {
      messages.push('Missing Check Out Time');
    }

    return { messages, completeness };
  };

  const testCancellation = (
    cancellationPolicy?: ICancellationPolicyOption['option'],
  ): { messages: string[]; completeness: number } => {
    if (cancellationPolicy) {
      return { messages: [], completeness: 2 };
    } else {
      return { messages: ['Missing Cancellation Policy'], completeness: 0 };
    }
  };

  const testDescriptionText = (listing: IListing): { messages: string[]; completeness: number } => {
    if (listing && listing.description && listing.description.length >= 150) {
      return { messages: [], completeness: 4 };
    } else {
      return { messages: ['Missing Description Text'], completeness: 0 };
    }
  };

  const testDirections = (site: ISite): { messages: string[]; completeness: number } => {
    let completeness = 0;
    const messages: string[] = [];

    if (!site) {
      return { messages: ['Missing Directions'], completeness };
    }

    if (site.coordinates) {
      if (site.coordinates.lat) {
        completeness += 2;
      } else {
        messages.push('Missing Latitude Coordinates');
      }

      if (site.coordinates.lon) {
        completeness += 2;
      } else {
        messages.push('Missing Longitude Coordinates');
      }
    }

    return { messages, completeness };
  };

  const testLengthOfStay = (listing: IListing): { messages: string[]; completeness: number } => {
    let completeness = 0;
    const messages: string[] = [];

    if (!listing) {
      return { messages: ['Missing Minimum Length of Stay', 'Missing Maximum Length of Stay'], completeness };
    }

    if (listing.minLengthOfStayInDays) {
      completeness += 2;
    } else {
      messages.push('Missing Minimum Length of Stay');
    }

    if (listing.maxLengthOfStayInDays) {
      completeness += 2;
    } else {
      messages.push('Missing Maximum Length of Stay');
    }

    return { messages, completeness };
  };

  const testBooking = (listing: IListing): { messages: string[]; completeness: number } => {
    let completeness = 0;
    const messages: string[] = [];

    if (!listing) {
      return { messages, completeness };
    }

    const bookingProcess = testBookingProcess(listing);
    completeness += bookingProcess.completeness;
    messages.push(...bookingProcess.messages);

    const bookingRange = testBookingRange(listing);
    completeness += bookingRange.completeness;
    messages.push(...bookingRange.messages);

    const lengthOfStay = testLengthOfStay(listing);
    completeness += lengthOfStay.completeness;
    messages.push(...lengthOfStay.messages);

    const checkInOut = testCheckInOut(listing);
    completeness += checkInOut.completeness;
    messages.push(...checkInOut.messages);

    return { messages, completeness };
  };

  const testLandOwner = (listing: IListing): { messages: string[]; completeness: number } => {
    const messages: string[] = [];
    const landOwner = listing.landOwner;
    if (!landOwner) {
      return { messages: ['Missing Land Owner'], completeness: 0 };
    }

    if (landOwner.self) {
      return { messages: [], completeness: 2 };
    }

    if (!landOwner.represent) {
      return { messages: ['Missing Land Owner Information'], completeness: 0 };
    }

    if (landOwner.lastName) {
      if (landOwner.private) {
        if (!missingAddress(landOwner)) {
          return { messages: [], completeness: 2 };
        } else {
          messages.push('Missing Land Owner Address');
        }
      } else {
        return { messages: [], completeness: 2 };
      }
    }

    return { messages: [...messages, 'Missing Complete Land Owner Information'], completeness: 0 };
  };

  const testName = (listing: IListing): { messages: string[]; completeness: number } => {
    if (listing && listing.name && listing.name.length >= 8) {
      return { messages: [], completeness: 2 };
    } else {
      return { messages: ['Missing Site Name'], completeness: 0 };
    }
  };

  const testPhotos = (listing: IListing): { messages: string[]; completeness: number } => {
    let completeness = 0;
    const messages: string[] = [];

    if (!listing) {
      return { messages: ['Missing or Incomplete set of Photos'], completeness };
    }

    if (listing.photos && listing.photos.length >= 3) {
      completeness += 6;
    } else {
      messages.push('Missing or Incomplete set of Photos');
    }

    return { messages, completeness };
  };

  const testTerrain = (landTypes?: ILandType['type'][]): { messages: string[]; completeness: number } => {
    if (landTypes && landTypes.length > 0) {
      return { messages: [], completeness: 4 };
    } else {
      return { messages: ['Missing Terrain Information'], completeness: 0 };
    }
  };

  const testDescription = (listing: IListing): { messages: string[]; completeness: number } => {
    let completeness = 0;
    const messages: string[] = [];

    if (!listing) {
      return { messages: ['Missing Listing'], completeness };
    }

    const name = testName(listing);
    completeness += name.completeness;
    messages.push(...name.messages);

    const descriptionTest = testDescriptionText(listing);
    completeness += descriptionTest.completeness;
    messages.push(...descriptionTest.messages);

    const landOwner = testLandOwner(listing);
    completeness += landOwner.completeness;
    messages.push(...landOwner.messages);

    const terrain = testTerrain(listing.landTypes);
    completeness += terrain.completeness;
    messages.push(...terrain.messages);

    const photos = testPhotos(listing);
    completeness += photos.completeness;
    messages.push(...photos.messages);

    return { messages, completeness };
  };

  const testUserName = (user: IUser): { messages: string[]; completeness: number } => {
    let completeness = 0;
    const messages: string[] = [];

    if (!user) {
      return { messages: ['Missing User Profile'], completeness };
    }

    if (user.firstName) {
      completeness += 2;
    } else {
      messages.push('Missing Profile First Name');
    }

    if (user.lastName) {
      completeness += 2;
    } else {
      messages.push('Missing Profile Last Name');
    }

    return { messages, completeness };
  };

  const testUserPhoto = (user: IUser): { messages: string[]; completeness: number } => {
    let completeness = 0;
    const messages: string[] = [];

    if (!user) {
      return { messages: ['Missing User Profile'], completeness };
    }

    if (user.photo?.key) {
      completeness += 4;
    } else {
      messages.push('Missing User Profile Photo');
    }

    return { messages, completeness };
  };

  const testUserPhone = (user: IUser): { messages: string[]; completeness: number } => {
    let completeness = 0;
    const messages: string[] = [];

    if (!user) {
      return { messages: ['Missing User Profile'], completeness };
    }

    if (user.phone) {
      completeness += 4;
    } else {
      messages.push('Missing Profile Phone Number');
    }

    return { messages, completeness };
  };

  export const userProfilePercentComplete = (user?: IUser): { messages: string[]; completeness: number } => {
    let completeness = 4;
    const messages: string[] = [];

    if (!user) {
      return { messages: ['User Profile Missing'], completeness };
    }

    const userName = testUserName(user);
    completeness += userName.completeness;
    messages.push(...userName.messages);

    const userPhoto = testUserPhoto(user);
    completeness += userPhoto.completeness;
    messages.push(...userPhoto.messages);

    const userPhone = testUserPhone(user);
    completeness += userPhone.completeness;
    messages.push(...userPhone.messages);

    return { messages, completeness };
  };

  export const sitesCompleted = (listing: IListing): boolean => {
    return !!(listing?.sites && Array.isArray(listing.sites) && listing.sites.length >= 1);
  };

  export const bookingProcessCompleted = (listing: IListing): boolean => {
    const completeness = testBookingProcess(listing).completeness;

    return completeness >= 4;
  };

  export const bookingRangeCompleted = (listing: IListing): boolean => {
    const completeness = testBookingRange(listing).completeness;

    return completeness >= 3;
  };

  export const checkInOutCompleted = (listing: IListing): boolean => {
    const completeness = testCheckInOut(listing).completeness;

    return completeness >= 4;
  };

  export const cancellationCompleted = (cancellationPolicy?: ICancellationPolicyOption['option']): boolean => {
    const completeness = testCancellation(cancellationPolicy).completeness;

    return completeness >= 2;
  };

  export const descriptionTextCompleted = (listing: IListing): boolean => {
    const completeness = testDescriptionText(listing).completeness;

    return completeness >= 4;
  };

  export const directionsCompleted = (listing: IListing): boolean => {
    const completeness = testDirections(listing).completeness;

    return completeness >= 4;
  };

  export const landOwnerCompleted = (listing: IListing): boolean => {
    const completeness = testLandOwner(listing).completeness;

    return completeness >= 2;
  };

  export const lengthOfStayCompleted = (listing: IListing): boolean => {
    const completeness = testLengthOfStay(listing).completeness;

    return completeness >= 4;
  };

  export const nameCompleted = (listing: IListing): boolean => {
    const completeness = testName(listing).completeness;

    return completeness >= 2;
  };

  export const photosCompleted = (listing: IListing): boolean => {
    const completeness = testPhotos(listing).completeness;

    return completeness >= 6;
  };

  export const multisiteCompleted = (listing: IListing): boolean => {
    if (listing && listing.sites && Array.isArray(listing.sites)) {
      return listing.sites?.length > 0;
    }
    return false;
  };

  export const bookingCompleted = (listing: IListing): boolean => {
    return (
      bookingProcessCompleted(listing) &&
      bookingRangeCompleted(listing) &&
      checkInOutCompleted(listing) &&
      lengthOfStayCompleted(listing)
    );
  };

  export const testExtraIsSet = (listing: IListing): boolean => {
    if (!listing.extras) {
      return false;
    }

    const testExtra = listing.extras.find((extra) => extra.label === 'extraTent');

    if (!testExtra || !testExtra.count || (!testExtra.price && testExtra.price !== 0)) {
      return false;
    }

    return testExtra.count > 0 && testExtra.price >= 0;
  };

  export const terrainCompleted = (landTypes?: ILandType['type'][]): boolean => {
    const completeness = testTerrain(landTypes).completeness;
    return completeness >= 4;
  };

  export const descriptionCompleted = (listing: IListing): boolean => {
    return (
      nameCompleted(listing) &&
      descriptionTextCompleted(listing) &&
      landOwnerCompleted(listing) &&
      terrainCompleted(listing.landTypes) &&
      photosCompleted(listing)
    );
  };

  export const userNameCompleted = (user: IUser): boolean => {
    return testUserName(user).completeness >= 4;
  };

  export const userPhotoCompleted = (user: IUser): boolean => {
    return testUserPhoto(user).completeness >= 4;
  };

  export const userPhoneCompleted = (user: IUser): boolean => {
    return testUserPhone(user).completeness >= 4;
  };

  export const userProfileCompleted = (user: IUser): boolean => {
    return userNameCompleted(user) && userPhotoCompleted(user) && userPhoneCompleted(user);
  };

  export const userCompleted = (user: IUser): boolean => {
    return userProfileCompleted(user) && userPhotoCompleted(user);
  };

  export const listingPercentComplete = (listing: IListing): { messages: string[]; completeness: number } => {
    let completeness = 0;
    const messages: string[] = [];

    if (!listing) {
      return { messages: ['Missing Site'], completeness };
    }

    const description = testDescription(listing);
    completeness += description.completeness;
    messages.push(...description.messages);

    const directions = testDirections(listing);
    completeness += directions.completeness;
    messages.push(...directions.messages);

    const profilePercentComplete = userProfilePercentComplete(listing.user);
    completeness += profilePercentComplete.completeness;
    messages.push(...profilePercentComplete.messages);

    const booking = testBooking(listing);
    completeness += booking.completeness;
    messages.push(...booking.messages);

    return { messages, completeness: Math.floor((completeness / 103) * 100) };
  };

  export const completed = (listing: IListing): boolean => {
    return listingPercentComplete(listing).completeness >= 100;
  };

  export const listingCompleteMessages = (listing: IListing): string[] => {
    const messages: string[] = [];

    if (!listing) {
      return ['Missing Site'];
    }

    messages.push(...testDescription(listing).messages);
    messages.push(...testDirections(listing).messages);
    messages.push(...userProfilePercentComplete(listing.user).messages);

    messages.push(...testBooking(listing).messages);

    return messages;
  };

  export const pricingCompleted = (listing: IListing): boolean => {
    return cancellationCompleted(listing.cancellationPolicy);
  };
}
